import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Rectangle,
} from 'recharts';
import './styles.scss';
import { useMediaQuery } from 'react-responsive';
import AppDatePicker from '../../../components/DatePicker/index';
import { LineWave } from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConversationsStart } from '../../../redux/conversations/conversations.actions';
import { convertDateToEnglish, getCalenderLastDays } from '../../../utils/date';
// import WebsiteSelect from 'components/WebsiteSelect';
import useTranslation from 'hooks/useTranslation';

const lastWeekDate = getCalenderLastDays(6, 'days');
const getSelectedWorkspace = ({ websitesData }) =>
  websitesData.selectedWorkspace;

const ConversationsStatisticsChart = ({ conversations, isFreePackage }) => {
  const [selectedChannelId, setSelectedChannelId] = useState();
  const selectedWorkspace = useSelector(getSelectedWorkspace);
  const { getTranslation, getAppLocale } = useTranslation([
    'STATISTICS',
    'COMMON',
  ]);
  const { t } = getTranslation();
  const locale = getAppLocale();

  const isLoadingConversations = conversations.loading || !selectedChannelId;

  const yAxisDirection = locale === 'en' ? 'left' : 'right';
  const reverseXAxis = locale === 'en';

  const CLOSED_CONVERSATION = t('CONVS_CHART_LEGEND_ENDED');
  const ACCEPTED_CONVERSATION = t('CONVS_CHART_LEGEND_ACCEPTED');
  const CLOSED_AGENT_CONVERSATION = t('CONVS_CHART_LEGEND_CLOSED_AGENT');
  const DENIED_CONVERSATION = t('CONVS_CHART_LEGEND_DENIED_CONVERSATION');

  const week = [
    { name: t('SATURDAY') },
    { name: t('SUNDAY') },
    { name: t('MONDAY') },
    { name: t('TUESDAY') },
    { name: t('WEDNESDAY') },
    { name: t('THURSDAY') },
    { name: t('FRIDAY') },
  ];

  const isTextRotateSize = useMediaQuery({
    query: '(max-width: 900px)',
  });

  const IsScreenSmallSize = useMediaQuery({
    query: '(max-width: 992px)',
  });

  const [selectedDate, setSelectedDate] = useState(lastWeekDate);
  const dispatch = useDispatch();

  const getSelectedStatistic = (selectedDate, channelId) => {
    if (!channelId) return;

    let isLastWeek = false;
    if (selectedDate === lastWeekDate) {
      isLastWeek = true;
    }
    let startDate = convertDateToEnglish(selectedDate[0], '-');
    let endDate = convertDateToEnglish(selectedDate[1], '-');
    dispatch(
      fetchConversationsStart({ startDate, endDate, isLastWeek, channelId })
    );
  };

  const getConversationsCount = (key) => {
    switch (key) {
      case CLOSED_CONVERSATION:
        return (
          !conversations.loading &&
          conversations.data.chart &&
          conversations.data.chart.close
        );

      case ACCEPTED_CONVERSATION:
        return (
          !conversations.loading &&
          conversations.data.chart &&
          conversations.data.chart.accepted
        );
      case CLOSED_AGENT_CONVERSATION:
        return (
          !conversations.loading &&
          conversations.data.chart &&
          conversations.data.chart.closedAgent
        );
      case DENIED_CONVERSATION:
        return (
          !conversations.loading &&
          conversations.data.chart &&
          conversations.data.chart.deniedChats
        );

      default:
        break;
    }
  };

  const getChartData = () => {
    if (
      !conversations.loading &&
      conversations.data.chart &&
      conversations.data.chart.chart.length > 0
    ) {
      return conversations.data.chart.chart;
    } else if (
      (!conversations.loading &&
        conversations.data.chart &&
        conversations.data.chart.chart.length <= 0) ||
      conversations.loading
    ) {
      return week;
    }
  };

  const handleChangeChannelId = (value) => {
    setSelectedChannelId(value);
    getSelectedStatistic(selectedDate, value);
  };

  const handleChangeDatePicker = (selectedDate) => {
    getSelectedStatistic(selectedDate, selectedChannelId);
  };

  const renderLegendText = (props) => {
    const { payload } = props;
    return (
      <>
        <ul className='recharts-default-legend'>
          {payload.map(({ dataKey, color }, index) => (
            <li
              key={`item-${index}`}
              className={'recharts-legend-item legend-item-' + index}
            >
              <svg width={14} height={14}>
                <Rectangle width={14} height={14} style={{ fill: color }} />
              </svg>
              <span className='recharts-legend-item-text'>
                <span className='number' style={{ color }}>
                  {getConversationsCount(dataKey)}
                </span>
                <span className='text' style={{ color }}>
                  {dataKey}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </>
    );
  };

  let initionalChartData = week.map((item, index) => {
    return {
      name: item.name,
      CONVS_CHART_LEGEND_ENDED: 0,
      CONVS_CHART_LEGEND_ACCEPTED: 0,
      CONVS_CHART_LEGEND_CLOSED_AGENT: 0,
      CONVS_CHART_LEGEND_DENIED_CONVERSATION: 0,
    };
  });

  const adaptChartDataByLanguage = (data) => {
    return data?.map((item) => ({
      name: t(item.name),
      [t('ACCEPTED_CONVERSATION')]: item.ACCEPTED_CONVERSATION,
      [t('CLOSED_AGENT_CONVERSATION')]: item.CLOSED_AGENT_CONVERSATION,
      [t('CLOSED_CONVERSATION')]: item.CLOSED_CONVERSATION,
      [t('DENIED_CONVERSATION')]: item.DENIED_CONVERSATION,
      [t('OPENED_CONVERSATION')]: item.OPENED_CONVERSATION,
    }));
  };

  const renderChart = (chartData) => {
    return (
      <>
        <ResponsiveContainer width='99%' className='barchart'>
          <BarChart
            data={
              isFreePackage
                ? initionalChartData
                : adaptChartDataByLanguage(chartData)
            }
            margin={{
              top: 5,
              right: 0,
              left: 0,
              bottom: 5,
            }}
          >
            <CartesianGrid
              stroke='#e6e6e6'
              strokeDasharray='8,3'
              strokeWidth={1}
              reversed
            />
            <XAxis
              dataKey='name'
              angle={isTextRotateSize ? -90 : 0}
              tickLine={false}
              interval={0}
              dy={10}
              tickSize={20}
              height={50}
              reversed={reverseXAxis}
            />
            <YAxis
              allowDecimals={false}
              orientation={yAxisDirection}
              axisLine={false}
              tickLine={false}
              tickSize={15}
            />
            <Tooltip />
            <Legend
              verticalAlign='top'
              align='right'
              wrapperStyle={{
                paddingBottom: '40px',
              }}
              content={renderLegendText}
            />
            <Bar
              dataKey={CLOSED_CONVERSATION}
              stackId='name'
              fill='#475467'
              legendType='square'
              barSize={23}
            />
            <Bar
              dataKey={ACCEPTED_CONVERSATION}
              stackId='name'
              fill='#47CD89'
              legendType='square'
              barSize={23}
            />
            <Bar
              dataKey={CLOSED_AGENT_CONVERSATION}
              stackId='name'
              fill='#98A2B3'
              legendType='square'
              barSize={23}
            />
            <Bar
              dataKey={DENIED_CONVERSATION}
              stackId='name'
              fill='#F97066'
              legendType='square'
              barSize={23}
            />
          </BarChart>
        </ResponsiveContainer>
      </>
    );
  };

  useEffect(() => {
    if (!!selectedWorkspace) {
      handleChangeChannelId(selectedWorkspace.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspace]);

  return (
    <>
      <section className='filters'>
        {/* select channel id */}
        {/* <div className='filters__channel_select'>
          <WebsiteSelect
            onChange={handleChangeChannelId}
            value={selectedChannelId}
            initSelectFirstWebsite={true}
          />
        </div> */}

        {/* select date range */}
        <AppDatePicker
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onClose={handleChangeDatePicker}
          isFreePackage={isFreePackage}
          position={IsScreenSmallSize ? 'bottom' : 'right'}
        />
      </section>
      <div style={{ position: 'relative' }}>
        <>
          {isLoadingConversations && (
            <div className={`${'overlay chart-overlay'}`}>
              {/* <Loader color='rgb(177, 14, 132)' height={50} width={50} /> */}
              <LineWave
                height={50}
                width={50}
                color='rgb(177, 14, 132)'
                ariaLabel='line-wave'
                wrapperStyle={{}}
                wrapperClass=''
                visible={true}
              />
            </div>
          )}

          {!conversations.loading &&
            conversations.data.chart &&
            conversations.data.chart.chart.length <= 0 && (
              <>
                <div className={`${'overlay chart-overlay'}`}>
                  {t('CONVS_CHART_LEGEND_NO_DATA')}
                </div>
              </>
            )}
          {renderChart(getChartData())}
        </>
      </div>
    </>
  );
};

export default React.memo(ConversationsStatisticsChart);
